(() => {
  if (window.MonoreeReviewConversion) {
    window.MonoreeReviewConversion.refresh();
    return;
  }

  const linkSelector = '[data-monoree-review-conversion]';
  const widgetSelector = '#judgeme_product_reviews, .jdgm-rev-widg, .jm-review-widget';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let frame = 0;
  let observedWidget = null;

  const number = (value) => {
    const parsed = Number.parseFloat(String(value ?? '').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const integer = (value) => {
    const parsed = Number.parseInt(String(value ?? '').replace(/[^0-9-]/g, ''), 10);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const badgeData = (root) => {
    const badge = root.querySelector('[data-monoree-judge-source] .jdgm-prev-badge');
    if (!badge) return null;

    return {
      rating: number(badge.dataset.averageRating || badge.querySelector('[data-score]')?.dataset.score),
      count: integer(badge.dataset.numberOfReviews),
    };
  };

  const widgetData = () => {
    const widget = document.querySelector(widgetSelector);
    if (!widget) return null;

    const legacy = widget.matches('.jdgm-rev-widg') ? widget : widget.querySelector('.jdgm-rev-widg');
    let rating = number(legacy?.dataset.averageRating);
    let count = integer(legacy?.dataset.numberOfReviews);

    const modernSummary = widget.querySelector('.jm-average-rating-display');
    if (modernSummary) {
      const values = [...modernSummary.querySelectorAll('p.jm-text')].map((node) => node.textContent.trim());
      rating = number(values[0]) || rating;
      count = integer(values[1]) || count;
    }

    const preview = widget.querySelector('.jdgm-prev-badge');
    if (preview) {
      rating = number(preview.dataset.averageRating || preview.querySelector('[data-score]')?.dataset.score) || rating;
      count = integer(preview.dataset.numberOfReviews) || count;
    }

    return { widget, rating, count };
  };

  const formatRating = (rating) => new Intl.NumberFormat(document.documentElement.lang || 'en', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(rating);

  const formatCount = (count) => new Intl.NumberFormat(document.documentElement.lang || 'en', {
    maximumFractionDigits: 0,
  }).format(count);

  const render = (root, liveData) => {
    const link = root.querySelector(linkSelector);
    if (!link) return;

    const fallback = badgeData(root);
    const rating = liveData?.rating || number(link.dataset.rating) || fallback?.rating || 0;
    const count = liveData?.count || integer(link.dataset.count) || fallback?.count || 0;

    if (!(rating > 0 && rating <= 5 && count > 0)) {
      link.hidden = true;
      root.hidden = true;
      return;
    }

    link.dataset.rating = String(rating);
    link.dataset.count = String(count);
    link.style.setProperty('--monoree-rating-percent', `${Math.min(100, Math.max(0, rating / 5 * 100))}%`);

    const ratingText = formatRating(rating);
    const countText = formatCount(count);
    const ratingNode = link.querySelector('[data-monoree-review-rating]');
    const countNode = link.querySelector('[data-monoree-review-count]');
    const labelNode = link.querySelector('[data-monoree-review-count-label]');

    if (ratingNode) ratingNode.textContent = ratingText;
    if (countNode) countNode.textContent = countText;
    if (labelNode) labelNode.textContent = link.dataset.countLabel || labelNode.textContent;

    const template = link.dataset.ariaTemplate || 'Rated {rating} out of 5. Reviews: {count}. Read reviews.';
    link.setAttribute('aria-label', template.replace('{rating}', ratingText).replace('{count}', countText));
    root.hidden = false;
    link.hidden = false;
  };

  const findTarget = () => {
    const direct = document.getElementById('judgeme_product_reviews');
    if (direct) return direct;

    const widget = document.querySelector('.jdgm-rev-widg, .jm-review-widget');
    if (!widget) return null;

    const container = widget.closest('[id], .shopify-section') || widget;
    if (!container.id) container.id = 'monoree-product-reviews';
    return container;
  };

  const focusTarget = (target) => {
    const focusableHeading = target.querySelector(
      '.jdgm-rev-widg__title, .jm-review-widget__title, [data-testid="review-widget-title"], h2, h3'
    );

    const focusNode = focusableHeading || target;
    if (!focusNode.hasAttribute('tabindex')) focusNode.setAttribute('tabindex', '-1');
    focusNode.focus({ preventScroll: true });
  };

  const refresh = () => {
    frame = 0;
    const liveData = widgetData();

    document.querySelectorAll('[data-monoree-review-root]').forEach((root) => render(root, liveData));

    const widget = liveData?.widget || null;
    if (widget !== observedWidget) {
      widgetObserver.disconnect();
      observedWidget = widget;
      if (widget) {
        widgetObserver.observe(widget, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['data-number-of-reviews', 'data-average-rating'],
        });
      }
    }
  };

  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(refresh);
  };

  const widgetObserver = new MutationObserver(schedule);
  const mainObserver = new MutationObserver((entries) => {
    const changed = entries.some((entry) =>
      [...entry.addedNodes, ...entry.removedNodes].some((node) =>
        node.nodeType === 1 &&
        (node.matches?.(`${linkSelector}, ${widgetSelector}`) || node.querySelector?.(`${linkSelector}, ${widgetSelector}`))
      )
    );
    if (changed) schedule();
  });

  document.addEventListener('click', (event) => {
    const link = event.target.closest(linkSelector);
    if (!link || event.defaultPrevented || event.button !== 0 ||
        event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = findTarget();
    if (!target) return;

    event.preventDefault();
    const targetId = target.id || 'monoree-product-reviews';
    if (!target.id) target.id = targetId;

    target.scrollIntoView({
      behavior: reduceMotion.matches ? 'auto' : 'smooth',
      block: 'start',
    });

    try {
      history.replaceState(history.state, '', `#${targetId}`);
    } catch {}

    window.setTimeout(() => focusTarget(target), reduceMotion.matches ? 0 : 450);
  });

  document.addEventListener('shopify:section:load', schedule);
  new MutationObserver(schedule).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['lang'],
  });

  const main = document.querySelector('main') || document.body;
  mainObserver.observe(main, { childList: true, subtree: true });

  window.MonoreeReviewConversion = { refresh: schedule };
  refresh();
})();
